import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { structuresApi } from '../api/structures.api'
import type { CreateStructureRequest, UpdateStructureRequest } from '@/shared/types/admin'

const STRUCTURES_KEY = ['structures']

export function useStructures() {
  return useQuery({
    queryKey: STRUCTURES_KEY,
    queryFn: structuresApi.getAll,
    select: (data) => [...data].sort((a, b) => a.nom.localeCompare(b.nom)),
  })
}

export function useStructuresActives() {
  return useQuery({
    queryKey: [...STRUCTURES_KEY, 'actives'],
    queryFn: structuresApi.getActives,
  })
}

export function useStructure(id: number | undefined) {
  return useQuery({
    queryKey: [...STRUCTURES_KEY, id],
    queryFn: () => structuresApi.getById(id!),
    enabled: !!id,
  })
}

export function useCreateStructure() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateStructureRequest) => structuresApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STRUCTURES_KEY }),
  })
}

export function useUpdateStructure() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateStructureRequest }) =>
      structuresApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STRUCTURES_KEY }),
  })
}

export function useDeleteStructure() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => structuresApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STRUCTURES_KEY }),
  })
}
